import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { useInventory } from '../../context/InventoryContext';
import Badge from '../ui/Badge';

const LowStockItems: React.FC = () => {
  const { products, isLowStock, getCategoryName, getProductTotalStock } = useInventory();
  
  const lowStockProducts = products
    .filter(product => isLowStock(product))
    .sort((a, b) => (getProductTotalStock(a.id) / a.min_stock_level) - (getProductTotalStock(b.id) / b.min_stock_level))
    .slice(0, 10);
  
  return (
    <Card className="overflow-hidden h-[28rem] transition-all duration-200 hover:shadow-md flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Low Stock Supplies</CardTitle>
        <Link 
          to="/supplies?filter=low" 
          className="text-sm text-blue-900 hover:underline flex items-center"
        >
          View All <ExternalLink className="h-3 w-3 ml-1" />
        </Link>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        {lowStockProducts.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-500 text-center">No low stock supplies.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="p-4 space-y-3">
            {lowStockProducts.map((product) => (
              <div key={product.id} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                <div className="flex items-center">
                  {product.image ? (
                    <div 
                      className="w-8 h-8 rounded overflow-hidden bg-gray-100 flex-shrink-0"
                      style={{ backgroundImage: `url(${product.image})`, backgroundSize: 'cover' }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      <span className="text-xs text-gray-500">N/A</span>
                    </div>
                  )}
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{product.name}</p>
                    <p className="text-xs text-gray-500">
                      {product.category_id ? getCategoryName(product.category_id) : 'Uncategorized'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Badge
                    variant={getProductTotalStock(product.id) === 0 ? 'danger' : 'warning'}
                    className="mr-2"
                  >
                    {getProductTotalStock(product.id)} / {product.min_stock_level} {product.unit_of_measure}
                  </Badge>
                  <Link
                    to={`/supplies/${product.id}`}
                    className="text-sm text-blue-900 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
            </div>
            {/* Scroll indicator */}
            {lowStockProducts.length > 6 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-1">
                <div className="text-xs text-gray-600">Scroll for more</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LowStockItems;